import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import styles from "../assets/styles/RandomDiversity2026Ticket.module.css";
import rdLogo from "../assets/images/rd-logo-black.png";
import MemoryStoryFields from "../components/MemoryStoryFields";
import { getMemoryStatus, isMissingMemory, memoryApiBase, memoryErrorMessage, memoryLookupErrorMessage, registerMemory, validateStory } from "../utils/MemoryExperience";
import MemoryLanguageChoice from '../components/MemoryLanguageChoice';
import {
  MAX_TICKET_IMAGE_EDGE,
  MAX_TICKET_IMAGE_BYTES,
  prepareTicketImage,
  stripImageDataUrlPrefix,
} from "../utils/TicketImageUpload";

export const EXPERIENCE_COPY = {
  color: {
    eyebrow: "COLOR EXTRACTION",
    title: "",
    accessibleTitle: "색 추출 이미지 등록",
    description:
      "마지막 시퀀스에서 이 이미지를 바라봅니다. 뇌파의 표상 유사성을 따라 색을 추출하고, 사진에 담긴 이야기는 한 편의 시와 감정지도로 남깁니다.",
    prompt: "측정할 사진을 선택해 주세요.",
    labelTitle: "이모션 백신 라벨",
    labelHint: "추출된 색과 함께 인쇄될 이름입니다. 10자 이내로 입력해 주세요.",
    flow: ["IMAGE", "EEG", "COLOR"],
    successTitle: "색 추출 준비가 끝났습니다.",
    successBody: "현장의 안내에 따라 색 추출 체험을 시작해 주세요.",
    accentClass: "color",
  },
  fragrance: {
    eyebrow: "FRAGRANCE EXTRACTION",
    title: "",
    accessibleTitle: "향 추출 이미지 등록",
    description:
      "향을 맡는 순간과 이미지를 바라보는 순간의 뇌파를 연결합니다. 가장 가까운 세 감각이 탑·미들·바텀 노트가 됩니다.",
    prompt: "향의 기준이 될 이미지를 등록해 주세요.",
    labelTitle: "프래그런스 라벨",
    labelHint: "완성된 향과 함께 인쇄될 이름입니다. 10자 이내로 입력해 주세요.",
    flow: ["IMAGE", "EEG", "TOP · MIDDLE · BASE"],
    successTitle: "향 추출 준비가 끝났습니다.",
    successBody: "현장의 안내에 따라 향 추출 체험을 시작해 주세요.",
    accentClass: "fragrance",
  },
};

const readAttribute = (attribute) => {
  if (attribute === null || attribute === undefined) {
    return "";
  }
  if (typeof attribute !== "object") {
    return attribute;
  }
  if ("S" in attribute) {
    return attribute.S;
  }
  if ("N" in attribute) {
    return attribute.N;
  }
  if ("BOOL" in attribute) {
    return attribute.BOOL;
  }
  return "";
};

const isInactiveTicket = (ticket) => {
  const activeFields = [ticket?.active, ticket?.isActive, ticket?.enabled];
  const hasInactiveFlag = activeFields.some((field) => {
    const value = readAttribute(field);
    return (
      value === false ||
      value === 0 ||
      ["false", "0", "n", "no", "inactive", "disabled"].includes(
        String(value).toLowerCase()
      )
    );
  });

  const status = String(
    readAttribute(ticket?.status || ticket?.ticketStatus)
  ).toLowerCase();

  return (
    hasInactiveFlag ||
    ["inactive", "disabled", "expired", "deactivated"].includes(status)
  );
};

const classifyLoadError = (error) => {
  const status = error?.response?.status;
  const message = String(
    error?.response?.data?.message || error?.message || ""
  ).toLowerCase();

  if (
    status === 410 ||
    /inactive|disabled|expired|deactivated|비활성|만료/.test(message)
  ) {
    return "inactive";
  }
  if (status === 404 || /ticket not found|invalid ticket|티켓.*없/.test(message)) {
    return "invalid";
  }
  return "loadError";
};

const useTicketExperience = ({ ticketId, expectedTicketType }) => {
  const [phase, setPhase] = useState(ticketId ? "checking" : "missing");
  const [storedTicket, setStoredTicket] = useState({
    label: "",
    imageUrl: "",
  });
  const requestVersion = useRef(0);

  const loadTicket = useCallback(async () => {
    if (!ticketId) {
      setPhase("missing");
      return;
    }

    const version = ++requestVersion.current;
    setPhase("checking");

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/getTicketForHolder`,
        { params: { ticketId } }
      );
      if (version !== requestVersion.current) {
        return;
      }

      const ticket = response.data || {};
      const actualType = String(readAttribute(ticket.ticketType));
      if (actualType !== String(expectedTicketType)) {
        setPhase("wrongType");
        return;
      }
      if (isInactiveTicket(ticket)) {
        setPhase("inactive");
        return;
      }

      const label = String(readAttribute(ticket.vialLabel) || "");
      const imageUrl = String(readAttribute(ticket.imageUrl) || "");
      setStoredTicket({ label, imageUrl });
      setPhase(ticket.submitted || label || imageUrl ? "submitted" : "ready");
    } catch (error) {
      if (version === requestVersion.current) {
        setPhase(classifyLoadError(error));
      }
    }
  }, [expectedTicketType, ticketId]);

  useEffect(() => {
    loadTicket();
    return () => {
      requestVersion.current += 1;
    };
  }, [loadTicket]);

  return {
    phase,
    setPhase,
    storedTicket,
    loadTicket,
  };
};

const StatusPanel = ({ phase, onRetry }) => {
  const statusContent = {
    checking: {
      title: "티켓을 확인하고 있어요.",
      body: "잠시만 기다려 주세요.",
    },
    missing: {
      title: "티켓 주소가 완전하지 않아요.",
      body: "티켓의 QR 코드를 다시 스캔해 주세요.",
    },
    invalid: {
      title: "티켓 정보를 찾을 수 없어요.",
      body: "QR 코드를 다시 확인하거나 현장 스태프에게 문의해 주세요.",
    },
    wrongType: {
      title: "다른 체험용 티켓이에요.",
      body: "색 추출과 향 추출 티켓의 QR 코드가 서로 다릅니다.",
    },
    inactive: {
      title: "지금은 사용할 수 없는 티켓이에요.",
      body: "아직 활성화되지 않았거나 사용 기간이 종료되었습니다.",
    },
    loadError: {
      title: "연결이 원활하지 않아요.",
      body: "네트워크 상태를 확인한 뒤 다시 시도해 주세요.",
    },
  }[phase];

  if (!statusContent) {
    return null;
  }

  return (
    <div
      className={styles.statusPanel}
      role={phase === "loadError" ? "alert" : "status"}
      aria-live="polite"
    >
      <span
        className={`${styles.statusOrb} ${
          phase === "checking" ? styles.statusOrbActive : ""
        }`}
        aria-hidden="true"
      />
      <h2>{statusContent.title}</h2>
      <p>{statusContent.body}</p>
      {phase === "loadError" ? (
        <button className={styles.secondaryButton} type="button" onClick={onRetry}>
          다시 확인하기
        </button>
      ) : null}
    </div>
  );
};

const formatFileSize = (bytes) => {
  if (!bytes) {
    return "";
  }
  return `${(bytes / 1024 / 1024).toFixed(1)}MB`;
};

function TicketExperience({
  experience = "color",
  expectedTicketType,
  ticketId,
}) {
  const copy = EXPERIENCE_COPY[experience] || EXPERIENCE_COPY.color;
  const { phase, setPhase, storedTicket, loadTicket } = useTicketExperience({
    ticketId,
    expectedTicketType,
  });

  const fileInputRef = useRef(null);
  const [image, setImage] = useState(null);
  const [label, setLabel] = useState("");
  const [isProcessingImage, setIsProcessingImage] = useState(false);
  const [imageError, setImageError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [story, setStory] = useState("");
  const [savedStory, setSavedStory] = useState(null);
  const [language, setLanguage] = useState(null);
  const [consent, setConsent] = useState(false);
  const [memoryState, setMemoryState] = useState("idle");
  const [memoryError, setMemoryError] = useState("");
  const [memoryBusy, setMemoryBusy] = useState(false);
  const [storyFrozen, setStoryFrozen] = useState(false);
  const frozenMemory = useRef(null);
  const activeMemoryRequest = useRef(null);
  const imageSaved = useRef(false);
  const mounted = useRef(true);
  const submitting = useRef(false);
  const isColor = experience === "color";

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      activeMemoryRequest.current?.abort();
    };
  }, []);

  const checkMemoryStatus = useCallback(async signal => {
    setMemoryBusy(true);
    setMemoryState("checking");
    setMemoryError("");
    setSavedStory(null);
    try {
      const data = await getMemoryStatus(ticketId, signal);
      if (!mounted.current || signal.aborted) return;
      setMemoryState(data.status);
      setLanguage(data.language || 'ko');
      setSavedStory(typeof data.story === "string" ? data.story : null);
      setMemoryError("");
    } catch (error) {
      if (!mounted.current || signal.aborted) return;
      if (isMissingMemory(error) && !frozenMemory.current) {
        setMemoryState("missing");
      } else {
        // Even a 404 can race a timed-out POST. Preserve the original input
        // until the server confirms an accepted request; retries use its snapshot.
        setMemoryState(frozenMemory.current ? "uncertain" : "lookupError");
        setMemoryError(memoryLookupErrorMessage(error, Boolean(frozenMemory.current)));
      }
    } finally {
      if (mounted.current && !signal.aborted) setMemoryBusy(false);
    }
  }, [ticketId]);

  useEffect(() => {
    if (!isColor || phase !== "submitted") return;
    const controller = new AbortController();
    activeMemoryRequest.current = controller;
    checkMemoryStatus(controller.signal);
    return () => controller.abort();
  }, [isColor, phase, checkMemoryStatus]);

  const refreshMemoryStatus = async () => {
    if (submitting.current) return;
    submitting.current = true;
    const controller = new AbortController();
    activeMemoryRequest.current = controller;
    try { await checkMemoryStatus(controller.signal); }
    finally { submitting.current = false; }
  };

  const submitMemory = async () => {
    // Never persist a visitor's story in browser storage. The in-memory snapshot
    // only protects retries on this QR; changing QR remounts this component.
    if (!language) { setMemoryError('결과물의 언어를 먼저 선택해 주세요.'); return; }
    if (!frozenMemory.current) frozenMemory.current = { ticketId, story: story.trim(), consent, language };
    setStoryFrozen(true);
    setMemoryBusy(true);
    setMemoryState("submitting");
    setMemoryError("");
    const controller = new AbortController();
    activeMemoryRequest.current = controller;
    try {
      const data = await registerMemory(frozenMemory.current, controller.signal);
      if (!mounted.current || controller.signal.aborted) return;
      setMemoryState(data.status);
      setSavedStory(frozenMemory.current.story);
      setMemoryError("");
    } catch (error) {
      if (!mounted.current || controller.signal.aborted) return;
      // A failed response does not prove that the server rejected the request.
      await checkMemoryStatus(controller.signal);
    } finally {
      if (mounted.current && !controller.signal.aborted) setMemoryBusy(false);
    }
  };

  const submitStoryOnly = async event => {
    event.preventDefault();
    if (submitting.current) return;
    const error = validateStory(story, consent);
    if (error) { setMemoryError(error); return; }
    try { memoryApiBase(); } catch (error) { setMemoryError(memoryErrorMessage(error)); return; }
    submitting.current = true;
    try {
      await submitMemory();
    } finally {
      submitting.current = false;
    }
  };

  const handleImageFile = async (file) => {
    if (!file || imageSaved.current) {
      return;
    }

    setImageError("");
    setSubmitError("");
    setIsProcessingImage(true);
    try {
      const preparedImage = await prepareTicketImage(file);
      setImage(preparedImage);
    } catch (error) {
      setImageError(
        error?.message ||
          "이미지를 처리하지 못했습니다. 다른 이미지로 다시 시도해 주세요."
      );
    } finally {
      setIsProcessingImage(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    if (phase !== "ready" || isProcessingImage) {
      return;
    }
    handleImageFile(event.dataTransfer.files?.[0]);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting.current) return;
    const trimmedLabel = label.trim();

    setSubmitError("");
    if (!image) {
      setImageError("먼저 이미지를 선택해 주세요.");
      return;
    }
    if (!trimmedLabel) {
      setSubmitError("라벨 이름을 입력해 주세요.");
      return;
    }
    if (isColor) {
      if (!language) { setSubmitError('결과물의 언어를 먼저 선택해 주세요.'); return; }
      const storyError = validateStory(story, consent);
      if (storyError) { setSubmitError(storyError); return; }
      try { memoryApiBase(); } catch (error) { setSubmitError(memoryErrorMessage(error)); return; }
    }

    submitting.current = true;
    setPhase("submitting");
    setUploadProgress(8);
    try {
      if (!imageSaved.current) {
        const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/updateTicketForHolder`,
        {
          ticketId,
          vialLabel: trimmedLabel,
          imageUrl: stripImageDataUrlPrefix(image.dataUrl),
        },
        {
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              setUploadProgress(
                Math.min(
                  95,
                  Math.round(
                    (progressEvent.loaded / progressEvent.total) * 100
                  )
                )
              );
            }
          },
        }
      );
        if (!response.data) throw new Error("UPDATE_REJECTED");
        imageSaved.current = true;
      }
      if (!mounted.current) return;
      setUploadProgress(100);
      setLabel(trimmedLabel);
      setPhase("success");
      if (isColor) await submitMemory();
    } catch (error) {
      if (!mounted.current) return;
      if (
        error?.response?.status === 409 &&
        error?.response?.data?.code === "ALREADY_SUBMITTED"
      ) {
        setUploadProgress(0);
        await loadTicket();
        return;
      }
      setPhase("ready");
      setSubmitError("전송하지 못했습니다. 네트워크 상태를 확인한 뒤 다시 시도해 주세요.");
      setUploadProgress(0);
    } finally {
      submitting.current = false;
    }
  };

  const isFormVisible = phase === "ready" || phase === "submitting";
  const isSubmitting = phase === "submitting";
  const completedLabel =
    phase === "submitted" ? storedTicket.label : label.trim();
  const completedImage =
    phase === "submitted" ? storedTicket.imageUrl : image?.dataUrl;

  return (
    <main
      className={`${styles.page} ${styles[copy.accentClass]}`}
      data-experience={experience}
    >
      <div className={styles.lightField} aria-hidden="true" />
      <div className={styles.dataStream} aria-hidden="true">
        <span />
        <span />
        <span />
        <span />
        <span />
      </div>

      <header className={styles.header}>
        <img
          className={styles.wordmarkLogo}
          src={rdLogo}
          alt="RANDOM DIVERSITY"
        />
        <span className={styles.concept}>COLOR / FRAGRANCE / SOUND</span>
      </header>

      <section className={styles.layout}>
        <div className={styles.introduction}>
          <p className={styles.eyebrow}>{copy.eyebrow}</p>
          {copy.title ? (
            <h1>
              {copy.title.split("\n").map((line) => (
                <span key={line}>{line}</span>
              ))}
            </h1>
          ) : (
            <h1 className={styles.visuallyHidden}>{copy.accessibleTitle}</h1>
          )}
          <p className={styles.description}>{copy.description}</p>

          <ol className={styles.flow} aria-label="데이터 처리 흐름">
            {copy.flow.map((item, index) => (
              <li key={item}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                {item}
              </li>
            ))}
          </ol>
        </div>

        <section className={styles.glassPanel} aria-label="티켓 이미지 등록">
          {!isFormVisible &&
          phase !== "submitted" &&
          phase !== "success" ? (
            <StatusPanel phase={phase} onRetry={loadTicket} />
          ) : null}

          {isFormVisible && isColor && !language ? <MemoryLanguageChoice onChoose={setLanguage} /> : null}
          {isFormVisible && (!isColor || language) ? (
            <form className={styles.form} onSubmit={handleSubmit} noValidate>
              {isColor ? <div className={styles.selectedLanguage}>
                <span>RESULT LANGUAGE · {language === 'en' ? 'English' : '한국어'}</span>
                <button type="button" className={styles.textButton} disabled={isSubmitting || storyFrozen || imageSaved.current} onClick={() => setLanguage(null)}>언어 변경</button>
              </div> : null}
              <div className={styles.panelHeading}>
                <span>01 / IMAGE</span>
                <h2>{copy.prompt}</h2>
                <p>
                  JPG, PNG, HEIC, HEIF · 원본 최대{" "}
                  {MAX_TICKET_IMAGE_BYTES / 1024 / 1024}MB
                  <br />
                  원본 비율 유지 · 긴 변 {MAX_TICKET_IMAGE_EDGE}px 이하로
                  자동 최적화
                </p>
              </div>

              <input
                ref={fileInputRef}
                className={styles.visuallyHidden}
                type="file"
                accept=".jpg,.jpeg,.png,.heic,.heif,image/jpeg,image/png,image/heic,image/heif"
                aria-label="체험에 사용할 이미지 선택"
                disabled={isSubmitting || isProcessingImage || imageSaved.current}
                onChange={(event) => handleImageFile(event.target.files?.[0])}
              />

              {image ? (
                <div className={styles.previewCard}>
                  <img
                    src={image.dataUrl}
                    alt="업로드한 이미지 미리보기"
                    className={styles.previewImage}
                  />
                  <div className={styles.previewMeta}>
                    <div>
                      <strong>{image.fileName}</strong>
                      <span>{formatFileSize(image.sourceSize)}</span>
                    </div>
                    <button
                    type="button"
                    className={styles.textButton}
                    disabled={isSubmitting || isProcessingImage || imageSaved.current}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      이미지 교체
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  className={`${styles.dropZone} ${
                    imageError ? styles.dropZoneError : ""
                  }`}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={handleDrop}
                >
                  <span className={styles.uploadGlyph} aria-hidden="true">
                    +
                  </span>
                  <strong>
                    {isProcessingImage
                      ? "이미지를 처리하고 있어요."
                      : "이미지를 선택해 주세요."}
                  </strong>
                  <p>여기에 끌어놓거나 버튼을 눌러 선택할 수 있습니다.</p>
                  <button
                    type="button"
                    className={styles.secondaryButton}
                    disabled={isProcessingImage || isSubmitting}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {isProcessingImage ? "처리 중…" : "이미지 선택"}
                  </button>
                </div>
              )}

              {imageError ? (
                <p className={styles.errorMessage} role="alert">
                  {imageError}
                </p>
              ) : null}

              <div className={styles.labelField}>
                <div className={styles.fieldNumber}>02 / LABEL</div>
                <label htmlFor={`${experience}-ticket-label`}>
                  {copy.labelTitle}
                </label>
                <p id={`${experience}-label-hint`}>{copy.labelHint}</p>
                <div className={styles.inputFrame}>
                  <input
                    id={`${experience}-ticket-label`}
                    type="text"
                    value={label}
                    maxLength={10}
                    required
                    disabled={isSubmitting || imageSaved.current}
                    aria-describedby={`${experience}-label-hint`}
                    aria-invalid={Boolean(submitError && !label.trim())}
                    placeholder={
                      experience === "color" ? "예: 여름의 파동" : "예: 투명한 오후"
                    }
                    onChange={(event) => {
                      setLabel(event.target.value);
                      setSubmitError("");
                    }}
                  />
                  <span aria-label={`${label.length}/10자`}>
                    {label.length}/10
                  </span>
                </div>
              </div>

              {isColor ? <MemoryStoryFields story={story} onStoryChange={setStory} consent={consent} onConsentChange={setConsent} disabled={isSubmitting} /> : null}

              {submitError ? (
                <p className={styles.errorMessage} role="alert">
                  {submitError}
                </p>
              ) : null}

              {isSubmitting ? (
                <div
                  className={styles.progressBlock}
                  role="status"
                  aria-live="polite"
                >
                  <div>
                    <span>데이터를 안전하게 전송하고 있어요.</span>
                    <strong>{uploadProgress}%</strong>
                  </div>
                  <progress max="100" value={uploadProgress}>
                    {uploadProgress}%
                  </progress>
                </div>
              ) : null}

              <button
                className={styles.primaryButton}
                type="submit"
                disabled={isSubmitting || isProcessingImage}
              >
                {isSubmitting ? "접수 중…" : isColor ? (imageSaved.current ? "이야기 접수 다시 시도" : "사진과 이야기 저장하기") : "이미지와 라벨 저장하기"}
              </button>
              <p className={styles.lockNotice}>
                전송 후에는 수정할 수 없습니다. {isColor ? "사진, 이야기와 라벨" : "이미지와 라벨"}을 확인해 주세요.
              </p>
            </form>
          ) : null}

          {phase === "submitted" || phase === "success" ? (
            <div className={styles.completion} role="status" aria-live="polite" aria-busy={isColor && memoryBusy}>
              <span className={styles.completionMark} aria-hidden="true">
                ✓
              </span>
              <p className={styles.completionEyebrow}>
                {phase === "submitted" ? "ALREADY REGISTERED" : "DATA RECEIVED"}
              </p>
              <h2>
                {phase === "submitted"
                  ? "이미 제출된 티켓입니다."
                  : copy.successTitle}
              </h2>
              <p>
                {phase === "submitted"
                  ? "등록된 정보는 수정할 수 없습니다. 현장 스태프의 안내를 따라 주세요."
                  : copy.successBody}
              </p>
              {completedImage ? (
                <img
                  src={completedImage}
                  alt="등록 완료된 이미지"
                  className={styles.completedImage}
                />
              ) : null}
              {completedLabel ? (
                <div className={styles.completedLabel}>
                  <span>LABEL</span>
                  <strong>{completedLabel}</strong>
                </div>
              ) : null}
              {isColor && savedStory ? (
                <section className={styles.completedStory} aria-label="등록한 이야기">
                  <span>STORY</span>
                  <p>{savedStory}</p>
                </section>
              ) : null}
              {isColor && ["missing", "uncertain", "lookupError"].includes(memoryState) ? <div className={styles.memoryRecovery}>
                {["missing", "uncertain"].includes(memoryState) ? <form onSubmit={submitStoryOnly} noValidate>
                  <p>{storyFrozen ? "사진은 저장되었습니다. 처음 접수한 이야기로만 다시 시도합니다." : "사진은 등록되어 있습니다. 사진에 담긴 이야기를 추가해 주세요."}</p>
                  {!language ? <MemoryLanguageChoice onChoose={setLanguage} /> : <>
                    <p>RESULT LANGUAGE · {language === 'en' ? 'English' : '한국어'}</p>
                    <MemoryStoryFields story={story} onStoryChange={setStory} consent={consent} onConsentChange={setConsent} disabled={memoryBusy || storyFrozen} />
                    <button type="submit" className={styles.primaryButton} disabled={memoryBusy}>{memoryBusy ? "접수 중…" : storyFrozen ? "이야기 접수 다시 시도" : "이야기 저장하기"}</button>
                  </>}
                </form> : null}
                {["lookupError", "uncertain"].includes(memoryState) ? <button type="button" className={styles.secondaryButton} onClick={refreshMemoryStatus} disabled={memoryBusy}>접수 상태 다시 확인하기</button> : null}
                {memoryError ? <p role="alert" className={styles.errorMessage}>{memoryError}</p> : null}
              </div> : null}
            </div>
          ) : null}
        </section>
      </section>

      <footer className={styles.footer}>
        <div>
          <span>RANDOM DIVERSITY · EEG REPRESENTATIONAL SIMILARITY</span>
          <small>
            표상 유사도 기반의 예술적 해석이며 감정 판독이나 의료 분석이
            아닙니다.
          </small>
        </div>
        <span>SEOUL, 2026</span>
      </footer>
    </main>
  );
}

export default function RandomDiversity2026Ticket({ experience = "color", expectedTicketType }) {
  const [searchParams] = useSearchParams();
  const ticketId = (searchParams.get("ticketId") || "").trim();
  return <TicketExperience key={`${experience}:${ticketId}`} experience={experience} expectedTicketType={expectedTicketType} ticketId={ticketId} />;
}
